import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import EmptyState from '../components/ui/EmptyState';
import { useToast } from '../context/ToastContext';
import ConfirmModal from '../components/ui/ConfirmModal';
import { useTranslation } from 'react-i18next';

// SVG Icons
const ArrowLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
  </svg>
);
const UploadIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
    />
  </svg>
);
const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
    />
  </svg>
);
const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
    />
  </svg>
);
const FolderIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
    />
  </svg>
);
const DocumentTextIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);
const SheetIcon = () => (
  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
    />
  </svg>
);
const XIcon = () => (
  <svg
    className="w-3.5 h-3.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2.5}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const FileIcon = ({ extension }) => {
  const colorMap = {
    pdf: 'bg-danger-50 dark:bg-danger-950/50 text-danger-600 dark:text-danger-300',
    doc: 'bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-300',
    docx: 'bg-primary-50 dark:bg-primary-950/50 text-primary-600 dark:text-primary-300',
    xls: 'bg-accent-50 dark:bg-accent-950/50 text-accent-600 dark:text-accent-300',
    xlsx: 'bg-accent-50 dark:bg-accent-950/50 text-accent-600 dark:text-accent-300',
    jpg: 'bg-warning-50 dark:bg-warning-950/50 text-warning-600 dark:text-warning-300',
    jpeg: 'bg-warning-50 dark:bg-warning-950/50 text-warning-600 dark:text-warning-300',
    png: 'bg-warning-50 dark:bg-warning-950/50 text-warning-600 dark:text-warning-300',
    txt: 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300',
  };
  const ext = (extension || '').toLowerCase();
  const colors =
    colorMap[ext] || 'bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400';
  return (
    <div
      className={`w-10 h-10 rounded-lg ${colors} flex items-center justify-center flex-shrink-0`}
    >
      <DocumentTextIcon />
    </div>
  );
};

const getFileExtension = (fileName) => {
  if (!fileName) return '';
  const parts = fileName.split('.');
  return parts.length > 1 ? parts.pop() : '';
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

// ── Combobox: dropdown cu tipuri existente + creare tip nou la scriere ──
const TypeCombobox = ({ value, onChange, options, placeholder, t }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value || '');
  const wrapperRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const lowerQ = query.toLowerCase();
  const filtered = options.filter((o) => o.name.toLowerCase().includes(lowerQ));
  const exactMatch = options.some((o) => o.name.toLowerCase() === lowerQ);
  const showCreate = query.trim() && !exactMatch;

  const select = (name) => {
    onChange(name);
    setQuery(name);
    setOpen(false);
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        type="text"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-700 rounded-lg bg-surface text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
      />
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filtered.map((opt) => (
            <button
              key={opt.id || `builtin-${opt.name}`}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => select(opt.name)}
              className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 dark:hover:bg-primary-950/50 flex items-center justify-between gap-2"
            >
              <span className="text-slate-700 dark:text-slate-300">{opt.name}</span>
              <span className="flex items-center gap-1.5">
                {opt.builtin && (
                  <span className="text-[10px] text-slate-400 dark:text-slate-500">
                    {t('documents.builtin')}
                  </span>
                )}
                {opt.isItmDefault && <Badge color="green">ITM</Badge>}
              </span>
            </button>
          ))}
          {showCreate && (
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => select(query.trim())}
              className="w-full text-left px-3 py-2 text-sm hover:bg-primary-50 dark:hover:bg-primary-950/50 border-t border-slate-200 dark:border-slate-800 text-primary-700 dark:text-primary-300 font-medium"
            >
              + {t('documents.createType', { name: query.trim() })}
            </button>
          )}
          {filtered.length === 0 && !showCreate && (
            <div className="p-3 text-sm text-slate-500 dark:text-slate-400 text-center">
              {t('documents.noTypesYet')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Multi-select angajați cu search și chips ──
const EmployeeMultiSelect = ({ employees, selectedIds, onChange, t }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const lowerQ = query.toLowerCase();
  const filtered = useMemo(() => {
    if (!lowerQ) return employees.slice(0, 100);
    return employees
      .filter((e) => {
        const name = `${e.user?.firstName || ''} ${e.user?.lastName || ''}`.toLowerCase();
        const job = (e.jobTitle || '').toLowerCase();
        const dept = (e.department?.name || '').toLowerCase();
        return name.includes(lowerQ) || job.includes(lowerQ) || dept.includes(lowerQ);
      })
      .slice(0, 100);
  }, [employees, lowerQ]);

  const selectedSet = new Set(selectedIds);
  const selectedEmployees = employees.filter((e) => selectedSet.has(e.id));

  const toggle = (id) => {
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <div
        onClick={() => setOpen(true)}
        className="min-h-[2.5rem] w-full px-2 py-1.5 border border-slate-300 dark:border-slate-700 rounded-lg bg-surface cursor-text flex flex-wrap items-center gap-1.5"
      >
        {selectedEmployees.map((e) => (
          <span
            key={e.id}
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-950/50 text-primary-700 dark:text-primary-300 text-xs"
          >
            {e.user?.firstName} {e.user?.lastName}
            <button
              type="button"
              onClick={(ev) => {
                ev.stopPropagation();
                toggle(e.id);
              }}
              className="hover:text-primary-900"
            >
              <XIcon />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          placeholder={
            selectedEmployees.length === 0 ? t('documents.searchEmployeePlaceholder') : ''
          }
          className="flex-1 min-w-[8rem] bg-transparent outline-none text-sm text-slate-900 dark:text-slate-100 placeholder-slate-400"
        />
      </div>
      {open && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-800 rounded-lg shadow-lg flex flex-col max-h-80">
          {/* Header fix cu buton OK — vizibil imediat când deschizi dropdown-ul */}
          <div className="flex items-center justify-between gap-2 px-3 py-2 bg-primary-600 dark:bg-primary-700 rounded-t-lg">
            <span className="text-sm font-medium text-white">
              {t('documents.selectedCount', { count: selectedIds.length })}
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="px-4 py-1.5 text-sm font-semibold rounded-md bg-white text-primary-700 hover:bg-primary-50 shadow-sm transition-colors"
            >
              {t('documents.ok')}
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-3 text-sm text-slate-500 dark:text-slate-400 text-center">
                {t('documents.noEmployeesFound')}
              </div>
            ) : (
              filtered.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onMouseDown={(ev) => ev.preventDefault()}
                  onClick={() => toggle(e.id)}
                  className={`w-full text-left px-3 py-2 text-sm hover:bg-primary-50 dark:hover:bg-primary-950/50 flex items-center gap-2 ${selectedSet.has(e.id) ? 'bg-primary-50/50 dark:bg-primary-950/30' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedSet.has(e.id)}
                    readOnly
                    className="pointer-events-none"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 dark:text-slate-200 truncate">
                      {e.user?.firstName} {e.user?.lastName}
                    </p>
                    <p className="text-xs text-slate-500 dark:text-slate-400 truncate">
                      {[e.jobTitle, e.department?.name].filter(Boolean).join(' · ') ||
                        t('documents.noDeptOrJob')}
                    </p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CompanyDocuments = ({ scopedCompanyId }) => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const params = useParams();
  const companyId = scopedCompanyId || params.companyId;
  const embedded = Boolean(scopedCompanyId);
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [sheets, setSheets] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [types, setTypes] = useState([]);
  const [activeTab, setActiveTab] = useState('documents');
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  // Upload form state
  const fileInputRef = useRef(null);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [typeName, setTypeName] = useState('');
  const [description, setDescription] = useState('');
  const [includeInItm, setIncludeInItm] = useState(false);
  const [assignToEmployees, setAssignToEmployees] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);

  // Filter state (listă)
  const [filterEmployeeId, setFilterEmployeeId] = useState('');
  const [filterTypeName, setFilterTypeName] = useState('');
  const [filterItmOnly, setFilterItmOnly] = useState(false);

  // Pre-populează includeInItm dacă tipul curent e marcat ITM by default
  useEffect(() => {
    if (!typeName) return;
    const match = types.find((t) => t.name.toLowerCase() === typeName.toLowerCase());
    if (match?.isItmDefault && !includeInItm) setIncludeInItm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeName, types]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchData = async () => {
    try {
      const [companyRes, docsRes, sheetsRes, empRes, typesRes] = await Promise.all([
        api.get(`/companies/${companyId}`),
        api.get(`/documents/${companyId}`),
        api.get(`/employee-sheets/${companyId}`),
        api.get('/employees', { params: { companyId } }),
        api.get('/document-types', { params: { companyId } }),
      ]);
      setCompany(companyRes.data);
      setDocuments(docsRes.data);
      setSheets(sheetsRes.data);
      setEmployees(empRes.data || []);
      setTypes(typesRes.data || []);
    } catch (err) {
      console.error(err);
      showToast(t('documents.loadError'), 'error');
      navigate('/companies');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    setSelectedFiles(Array.from(e.target.files || []));
  };

  const handleClearFiles = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setFileInputKey((k) => k + 1);
  };

  const resetForm = () => {
    setSelectedFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setFileInputKey((k) => k + 1);
    setTypeName('');
    setDescription('');
    setIncludeInItm(false);
    setAssignToEmployees(false);
    setSelectedEmployeeIds([]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      showToast(t('documents.selectFileError'), 'error');
      return;
    }
    if (!typeName.trim()) {
      showToast(t('documents.typeRequired'), 'error');
      return;
    }
    if (assignToEmployees && selectedEmployeeIds.length === 0) {
      showToast(t('documents.employeeRequired'), 'error');
      return;
    }

    setUploading(true);
    const fd = new FormData();
    selectedFiles.forEach((f) => fd.append('documents', f));
    fd.append('companyId', companyId);
    fd.append('typeName', typeName.trim());
    fd.append('description', description);
    fd.append('includeInItmReport', String(includeInItm));
    if (assignToEmployees && selectedEmployeeIds.length > 0) {
      fd.append('employeeIds', JSON.stringify(selectedEmployeeIds));
    }

    try {
      const res = await api.post('/documents/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(t('documents.uploadSuccess', { count: res.data.created }), 'success');
      resetForm();
      fetchData();
    } catch (err) {
      showToast(err.response?.data?.error || t('documents.uploadError'), 'error');
    } finally {
      setUploading(false);
    }
  };

  const [deleteDocId, setDeleteDocId] = useState(null);
  const [deletingDoc, setDeletingDoc] = useState(false);
  const confirmDeleteDoc = async () => {
    if (!deleteDocId) return;
    setDeletingDoc(true);
    try {
      await api.delete(`/documents/${deleteDocId}`);
      setDeleteDocId(null);
      fetchData();
    } catch (err) {
      showToast(t('documents.deleteError'), 'error');
    } finally {
      setDeletingDoc(false);
    }
  };

  const handleDownloadSheet = async (trainingId, certificateNumber, employeeName) => {
    try {
      const response = await api.get(`/certificates/${trainingId}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Fisa-SSM-${employeeName}-${certificateNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showToast(t('documents.sheetDownloadError'), 'error');
    }
  };

  const handleViewSheet = async (trainingId) => {
    try {
      const response = await api.get(`/certificates/${trainingId}/download?inline=true`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(
        new Blob([response.data], { type: 'application/pdf' })
      );
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      showToast(t('sheets.openSheetError'), 'error');
    }
  };

  const handleDownloadDocument = async (docId, fallbackName) => {
    try {
      const response = await api.get(`/documents/${docId}/download`, { responseType: 'blob' });
      // Extrage numele din Content-Disposition dacă e prezent
      const cd = response.headers['content-disposition'] || '';
      const match = cd.match(/filename="?([^"]+)"?/);
      const downloadName = match ? decodeURIComponent(match[1]) : fallbackName;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
      showToast(t('documents.downloadError'), 'error');
    }
  };

  const filteredDocuments = useMemo(() => {
    return documents.filter((d) => {
      if (filterEmployeeId === '__company__' && d.employeeId) return false;
      if (
        filterEmployeeId &&
        filterEmployeeId !== '__company__' &&
        d.employeeId !== filterEmployeeId
      )
        return false;
      if (filterTypeName && (d.category || '').toLowerCase() !== filterTypeName.toLowerCase())
        return false;
      if (filterItmOnly && !d.includeInItmReport) return false;
      return true;
    });
  }, [documents, filterEmployeeId, filterTypeName, filterItmOnly]);

  if (loading) return <Spinner size="lg" label={t('documents.loading')} />;

  const effectiveTab = embedded ? 'documents' : activeTab;
  const uniqueTypesInList = [...new Set(documents.map((d) => d.category).filter(Boolean))].sort();

  return (
    <div className="space-y-6">
      {!embedded && (
        <Breadcrumbs
          items={[
            { label: t('documents.breadcrumbCompanies'), href: '/companies' },
            {
              label: company?.name || t('documents.companyFallback'),
              href: company?.id ? `/companies/${company.id}` : undefined,
            },
            { label: t('documents.title') },
          ]}
        />
      )}
      {!embedded && (
        <PageHeader
          title={t('documents.pageTitle', { name: company?.name })}
          subtitle={t('documents.pageSubtitle')}
          actions={
            <Button variant="ghost" size="sm" onClick={() => navigate('/companies')}>
              <ArrowLeftIcon /> {t('documents.backToCompanies')}
            </Button>
          }
        />
      )}

      {!embedded && (
        <Card padding={false}>
          <div className="flex border-b border-slate-200 dark:border-slate-800">
            <button
              onClick={() => setActiveTab('documents')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium text-sm transition-colors ${
                activeTab === 'documents'
                  ? 'text-primary-600 dark:text-primary-300 border-b-2 border-primary-600 dark:border-primary-700'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700'
              }`}
            >
              <FolderIcon /> {t('documents.documentsCount', { count: documents.length })}
            </button>
            <button
              onClick={() => setActiveTab('sheets')}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-medium text-sm transition-colors ${
                activeTab === 'sheets'
                  ? 'text-primary-600 dark:text-primary-300 border-b-2 border-primary-600 dark:border-primary-700'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-400 hover:text-slate-700'
              }`}
            >
              <SheetIcon /> {t('documents.sheetsCount', { count: sheets.length })}
            </button>
          </div>
        </Card>
      )}

      {effectiveTab === 'documents' ? (
        <>
          {/* Upload Form */}
          <Card>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              {t('documents.uploadNew')}
            </h2>
            <form onSubmit={handleUpload} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    {t('documents.type')} *
                  </label>
                  <TypeCombobox
                    value={typeName}
                    onChange={setTypeName}
                    options={types}
                    placeholder={t('documents.typePlaceholder')}
                    t={t}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    {t('documents.files')} *
                  </label>
                  <input
                    key={fileInputKey}
                    ref={fileInputRef}
                    type="file"
                    multiple
                    onChange={handleFileSelect}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-surface text-slate-900 dark:text-slate-100 text-sm file:mr-3 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 dark:file:bg-primary-950/50 file:text-primary-700 dark:file:text-primary-300 hover:file:bg-primary-100"
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.webp"
                  />
                  {selectedFiles.length > 0 && (
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <p className="text-xs text-slate-500 dark:text-slate-400">
                        {t('documents.filesSelected', { count: selectedFiles.length })}
                      </p>
                      <button
                        type="button"
                        onClick={handleClearFiles}
                        className="text-xs text-danger-600 dark:text-danger-300 hover:underline font-medium"
                      >
                        {t('documents.cancelFiles')}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                  {t('documents.description')}
                </label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2.5 border border-slate-300 dark:border-slate-700 rounded-lg bg-surface text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors text-sm"
                  placeholder={t('documents.descriptionPlaceholder')}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeInItm}
                    onChange={(e) => setIncludeInItm(e.target.checked)}
                    className="rounded"
                  />
                  {t('documents.includeInItm')}
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={assignToEmployees}
                    onChange={(e) => {
                      setAssignToEmployees(e.target.checked);
                      if (!e.target.checked) setSelectedEmployeeIds([]);
                    }}
                    className="rounded"
                  />
                  {t('documents.assignToEmployees')}
                </label>
              </div>

              {assignToEmployees && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                    {t('documents.employees')} *
                  </label>
                  <EmployeeMultiSelect
                    employees={employees}
                    selectedIds={selectedEmployeeIds}
                    onChange={setSelectedEmployeeIds}
                    t={t}
                  />
                  {selectedEmployeeIds.length > 0 && selectedFiles.length > 0 && (
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                      {t('documents.willCreateCount', {
                        count: selectedFiles.length * selectedEmployeeIds.length,
                      })}
                    </p>
                  )}
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={uploading}
                className="w-full"
              >
                <UploadIcon /> {uploading ? t('documents.uploading') : t('documents.uploadBtn')}
              </Button>
            </form>
          </Card>

          {/* Filters */}
          <Card>
            <div className="mb-3 flex items-center gap-2">
              <svg
                className="w-5 h-5 text-primary-600 dark:text-primary-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                />
              </svg>
              <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
                {t('documents.viewDocuments')}
              </h3>
              <span className="text-xs text-slate-500 dark:text-slate-400">
                — {t('documents.viewDocumentsHint')}
              </span>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="flex-1 min-w-[180px]">
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  {t('documents.filterByEmployee')}
                </label>
                <select
                  value={filterEmployeeId}
                  onChange={(e) => setFilterEmployeeId(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-surface text-sm"
                >
                  <option value="">{t('documents.allDocuments')}</option>
                  <option value="__company__">{t('documents.companyWideOnly')}</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.user?.firstName} {e.user?.lastName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="block text-xs font-medium text-slate-600 dark:text-slate-400 mb-1">
                  {t('documents.filterByType')}
                </label>
                <select
                  value={filterTypeName}
                  onChange={(e) => setFilterTypeName(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-700 rounded-lg bg-surface text-sm"
                >
                  <option value="">{t('documents.allTypes')}</option>
                  {uniqueTypesInList.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300 cursor-pointer pb-2">
                <input
                  type="checkbox"
                  checked={filterItmOnly}
                  onChange={(e) => setFilterItmOnly(e.target.checked)}
                  className="rounded"
                />
                {t('documents.onlyItm')}
              </label>
            </div>
          </Card>

          {/* Documents List */}
          <Card>
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
              {t('documents.documentsCount', { count: filteredDocuments.length })}
            </h2>
            {filteredDocuments.length === 0 ? (
              <EmptyState
                icon={<FolderIcon />}
                title={t('documents.noDocs')}
                description={t('documents.noDocsHint')}
              />
            ) : (
              <div className="space-y-2">
                {filteredDocuments.map((doc) => {
                  const employeeName = doc.employee
                    ? `${doc.employee.user?.firstName || ''} ${doc.employee.user?.lastName || ''}`.trim()
                    : null;
                  return (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-slate-800 hover:bg-surface-secondary transition-colors"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <FileIcon extension={getFileExtension(doc.fileName)} />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-slate-900 dark:text-slate-100 text-sm truncate">
                            {doc.fileName}
                          </h3>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            {doc.category && (
                              <Badge color={doc.includeInItmReport ? 'green' : 'blue'}>
                                {doc.category}
                              </Badge>
                            )}
                            {doc.includeInItmReport && (
                              <span className="text-[10px] text-green-700 dark:text-emerald-300 bg-green-50 dark:bg-slate-800/60 border border-green-200 dark:border-slate-700 rounded px-1.5 py-0.5 font-medium">
                                ITM
                              </span>
                            )}
                            {employeeName && <Badge color="purple">{employeeName}</Badge>}
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {formatFileSize(doc.fileSize)}
                            </span>
                            <span className="text-xs text-slate-500 dark:text-slate-400">
                              {new Date(doc.createdAt).toLocaleDateString('ro-RO')}
                            </span>
                          </div>
                          {doc.description && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 truncate">
                              {doc.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 ml-3 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadDocument(doc.id, doc.fileName)}
                          className="text-accent-600 dark:text-accent-300 hover:text-accent-700 hover:bg-accent-50"
                        >
                          <DownloadIcon />
                          <span className="hidden sm:inline">{t('common.download')}</span>
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteDocId(doc.id)}
                          className="text-danger-600 dark:text-danger-300 hover:text-danger-700 hover:bg-danger-50"
                        >
                          <TrashIcon />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </>
      ) : (
        // Fise SSM Tab
        <Card>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">
            {t('documents.generatedSheets', { count: sheets.length })}
          </h2>
          {sheets.length === 0 ? (
            <EmptyState
              icon={<SheetIcon />}
              title={t('documents.noSheets')}
              description={t('documents.noSheetsHint')}
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="table-header text-left">{t('documents.thSheetNo')}</th>
                    <th className="table-header text-left">{t('documents.thEmployee')}</th>
                    <th className="table-header text-left">{t('documents.thCourse')}</th>
                    <th className="table-header text-left">{t('common.date')}</th>
                    <th className="table-header text-left">{t('documents.thScore')}</th>
                    <th className="table-header text-right">{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sheets.map((sheet) => (
                    <tr key={sheet.id} className="table-row">
                      <td className="table-cell font-mono text-sm">{sheet.certificateNumber}</td>
                      <td className="table-cell">
                        <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
                          {sheet.training.employee.user.firstName}{' '}
                          {sheet.training.employee.user.lastName}
                        </span>
                      </td>
                      <td className="table-cell text-sm text-slate-600 dark:text-slate-300">
                        {sheet.training.course.title}
                      </td>
                      <td className="table-cell text-sm text-slate-600 dark:text-slate-300">
                        {new Date(sheet.issueDate).toLocaleDateString('ro-RO')}
                      </td>
                      <td className="table-cell">
                        <Badge color="green">{sheet.training.score}%</Badge>
                      </td>
                      <td className="table-cell text-right">
                        <div className="inline-flex items-center gap-1 justify-end">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleViewSheet(sheet.trainingId)}
                            className="text-primary-600 dark:text-primary-300 hover:text-primary-700"
                            title={t('sheets.viewSheetTooltip')}
                          >
                            <svg
                              className="w-3.5 h-3.5 inline-block mr-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth={2}
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                            </svg>
                            {t('sheets.viewSheet')}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              handleDownloadSheet(
                                sheet.trainingId,
                                sheet.certificateNumber,
                                `${sheet.training.employee.user.firstName}-${sheet.training.employee.user.lastName}`
                              )
                            }
                            className="text-slate-600 dark:text-slate-300 hover:text-slate-800"
                          >
                            <DownloadIcon /> {t('common.download')}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      <ConfirmModal
        open={!!deleteDocId}
        onClose={() => setDeleteDocId(null)}
        onConfirm={confirmDeleteDoc}
        title={t('documents.deleteTitle')}
        message={t('documents.deleteDocMsg')}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        variant="danger"
        loading={deletingDoc}
      />
    </div>
  );
};

export default CompanyDocuments;
